"use client";

import React, { useEffect, useRef, useState } from "react";
import { Leaf, Droplets, Sun, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const initiatives = [{
  id: 1,
  title: "2022: Landmark Corporate Merger",
  description: "Successfully navigated a multi-billion dollar acquisition for a global tech leader, setting a new precedent for antitrust compliance and cross-border regulatory approval.",
  category: "Corporate Law",
  icon: Leaf,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/corporate_merger_victory.png",
  link: "/LegalInsightsPage"
}, {
  id: 2,
  title: "2019: Supreme Court Victory",
  description: "Secured a unanimous decision in a high-stakes intellectual property dispute, protecting the creative rights of innovators across the national landscape.",
  category: "Litigation",
  icon: Droplets,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/supreme_court_win.png",
  link: "/LegalInsightsPage"
}, {
  id: 3,
  title: "2015: Real Estate Development",
  description: "Facilitated the legal framework for the city's largest sustainable urban development project, balancing commercial interests with community environmental standards.",
  category: "Real Estate",
  icon: Sun,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/real_estate_development.png",
  link: "/LegalInsightsPage"
}];
const AnimatedBlock = ({
  children,
  className,
  "data-index": dataIndex
}: {
  children: React.ReactNode;
  className?: string;
  "data-index"?: number;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref as React.RefObject<HTMLDivElement>} data-index={dataIndex} className={cn("transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", className)}>
      {children}
    </div>;
};
export default function AdvocacyInAction() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="263239" id="advocacy-in-action" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className={cn("max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)]", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <Badge variant="outline" className="mb-4 border-primary text-primary font-mono tracking-widest uppercase">
            Our Legacy
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Advocacy in Action: A History of Success
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Sterling Law Associates maintains a distinguished record of legal excellence. 
            Explore the milestones that define our commitment to precision and justice.
          </p>
        </div>

        {}
        <div className="space-y-20 md:space-y-32 relative">
          {}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-secondary/30 -translate-x-1/2 hidden lg:block" />
          
          {initiatives.map((initiative, index) => {
          const Icon = initiative.icon;
          const isEven = index % 2 === 0;
          return <AnimatedBlock key={initiative.id} data-index={index} className="group">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                  {}
                  <div className={cn("relative rounded-md overflow-hidden shadow-xl border border-secondary/20", isEven ? "lg:order-1" : "lg:order-2")}>
                    <AspectRatio ratio={4 / 3}>
                      <img src={initiative.image} alt={initiative.title} data-wvc-srcset={`${initiative.image}-wvc-srcset`} data-wvc-sizes={`${initiative.image}-wvc-sizes`} loading="lazy" className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-105 grayscale hover:grayscale-0" />
                      <div className="absolute inset-0 bg-primary/5 mix-blend-multiply" />
                    </AspectRatio>
                  </div>

                  {}
                  <div className={cn("flex flex-col items-start", isEven ? "lg:order-2 lg:pl-8" : "lg:order-1 lg:pr-8 lg:items-end lg:text-right")}>
                    {index > 2 && <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-md mb-6 border border-primary/20">
                        <Icon className="size-6" />
                      </div>}
                    <Badge variant="secondary" className="mb-4 font-mono tracking-wider bg-secondary/10 text-secondary-foreground">
                      {initiative.category}
                    </Badge>
                    <h3 className="text-2xl md:text-3xl font-bold font-default text-primary mb-4 italic">
                      {initiative.title}
                    </h3>
                    <p className="text-muted-foreground text-lg mb-8 leading-relaxed font-default">
                      {initiative.description}
                    </p>
                    <Button asChild size="lg" className="group/btn bg-primary hover:bg-primary/90 text-primary-foreground rounded-md shadow-md">
                      <Link to={initiative.link}>
                        Case Details
                        <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </AnimatedBlock>;
        })}
        </div>
      </div>
    </section>;
}